import type { FC, CSSProperties } from "react"
import { useMemo } from "react"
import { BLOCKS, INLINES } from "@contentful/rich-text-types"
import type { Options } from "@contentful/rich-text-react-renderer"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import Link from "~/components/Link"
import { ANALYTICS_EVENTS } from "~/helpers/analyticsEvents"

interface Props {
  styles?: {
    hyperlinkStyles?: CSSProperties
  }
  classes?: {
    paragraphClass?: string
    heading2Class?: string
    heading3Class?: string
    hyperlinkClass?: string
    listItemClass?: string
    unorderedListClass?: string
  }
  origin?: string
  openLinksInNewWindow?: boolean
  data: any
}

const ContentfulRichTextRenderer: FC<Props> = ({
  classes,
  styles,
  data,
  origin,
  openLinksInNewWindow
}) => {
  const options = useMemo<Options>(
    () => ({
      renderNode: {
        [BLOCKS.UL_LIST]: (_, children) => (
          <ul className={classes?.unorderedListClass}>{children}</ul>
        ),
        [BLOCKS.LIST_ITEM]: (_, children) => (
          <li className={classes?.listItemClass}>{children}</li>
        ),
        [BLOCKS.PARAGRAPH]: (_, children) => (
          <p className={classes?.paragraphClass}>{children}</p>
        ),
        [BLOCKS.HEADING_2]: (_, children) => (
          <h2 className={classes?.heading2Class}>{children}</h2>
        ),
        [BLOCKS.HEADING_3]: (_, children) => (
          <h3 className={classes?.heading3Class}>{children}</h3>
        ),
        [INLINES.HYPERLINK]: (node, children) => (
          <Link
            className={classes?.hyperlinkClass}
            style={styles?.hyperlinkStyles}
            to={node.data.uri}
            trackClick={
              origin
                ? () => ({
                    event: ANALYTICS_EVENTS.LINK_CLICKED,
                    data: {
                      url: node.data.uri,
                      path: window.location.pathname,
                      origin
                    }
                  })
                : undefined
            }
            openInNewWindow={openLinksInNewWindow}
          >
            {children}
          </Link>
        )
      }
    }),
    [
      styles?.hyperlinkStyles,
      classes?.heading2Class,
      classes?.heading3Class,
      classes?.hyperlinkClass,
      classes?.paragraphClass,
      origin,
      openLinksInNewWindow,
      classes?.unorderedListClass,
      classes?.listItemClass
    ]
  )

  const renderResult = useMemo(
    () => documentToReactComponents(data, options),
    [data, options]
  )

  return <div className="space-y-2">{renderResult}</div>
}

export default ContentfulRichTextRenderer
